import { useCallback, useEffect, useState } from "react"
import { Client } from "@xmpp/client"
import AdHocCommands, { CarRouteConfig } from "../api/adHocCommands"
import XmppPubSubApi from "../api/xmppPubSubApi"
import { useAppDispatch, useAppSelector } from "../reducers/hooks"
import {
  findRouteConfig,
  updateRouteConfig as updateRouteConfigAction,
} from "../reducers/routes2gSlice"

/**
 * Hook to manage car route configuration for a driver
 */
const useCarRouteConfig = (
  client: Client | undefined,
  routeJid: string | null | undefined,
) => {
  const dispatch = useAppDispatch()
  const carRouteConfig = useAppSelector(findRouteConfig(routeJid || undefined))
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchCarRouteConfig = useCallback(async () => {
    if (!client || !routeJid) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const config = await XmppPubSubApi.fetchCarConfig(client, routeJid)
      if (config) {
        dispatch(updateRouteConfigAction({ jid: routeJid, config }))
      }
      setIsLoading(false)
    } catch (err) {
      console.error("Error fetching car route config:", err)
      setError(err instanceof Error ? err : new Error(String(err)))
      setIsLoading(false)
    }
  }, [client, routeJid, dispatch])

  const updateCarConfig = useCallback(
    async (config: Partial<CarRouteConfig>) => {
      if (!client || !routeJid) return

      setIsLoading(true)
      setError(null)

      try {
        // Send update to server first
        await AdHocCommands.updateCarConfig(client, routeJid, config)

        // Wait a bit for server to process the update
        await new Promise((resolve) => setTimeout(resolve, 500))

        // Then fetch fresh config from server to ensure we have the latest state
        await fetchCarRouteConfig()
      } catch (err) {
        console.error("Error updating car route config:", err)
        setError(err instanceof Error ? err : new Error(String(err)))
        // Fetch current state from server on error
        await fetchCarRouteConfig()
      } finally {
        setIsLoading(false)
      }
    },
    [client, routeJid, fetchCarRouteConfig],
  )

  const updateRouteConfig = useCallback(
    async (config: Partial<CarRouteConfig>) => {
      if (!client || !routeJid) return

      setIsLoading(true)
      setError(null)

      try {
        // Send update to server first
        await AdHocCommands.updateRouteConfig(client, routeJid, config)

        // Then update Redux state by merging with the existing config — the
        // reducer is a full replace, so dispatching just the partial would
        // wipe fields the caller didn't touch (e.g. vehicle dimensions),
        // briefly tripping the "car not configured" warning until something
        // refetches.
        dispatch(
          updateRouteConfigAction({
            jid: routeJid,
            config: { ...carRouteConfig, ...config },
          }),
        )
      } catch (err) {
        console.error("Error updating car route config:", err)
        setError(err instanceof Error ? err : new Error(String(err)))
        // Fetch current state from server on error
        await fetchCarRouteConfig()
      } finally {
        setIsLoading(false)
      }
    },
    [client, routeJid, carRouteConfig, fetchCarRouteConfig, dispatch],
  )

  const clearCarConfig = useCallback(async () => {
    if (!client || !routeJid) return

    setIsLoading(true)
    setError(null)

    try {
      // Create an empty config object and send to server
      const emptyConfig: CarRouteConfig = {
        width: undefined,
        height: undefined,
        length: undefined,
        weight: undefined,
        travelMode: undefined,
        loadType: [],
      }
      await AdHocCommands.updateCarConfig(client, routeJid, emptyConfig)

      // Wait a bit for server to process the update
      await new Promise((resolve) => setTimeout(resolve, 500))

      // Then fetch fresh config from server to ensure we have the latest state
      await fetchCarRouteConfig()
    } catch (err) {
      console.error("Error clearing car route config:", err)
      setError(err instanceof Error ? err : new Error(String(err)))
      // Fetch current state from server on error
      await fetchCarRouteConfig()
    } finally {
      setIsLoading(false)
    }
  }, [client, routeJid, fetchCarRouteConfig])

  useEffect(() => {
    fetchCarRouteConfig()
  }, [fetchCarRouteConfig])

  return {
    carRouteConfig,
    isLoading,
    error,
    updateCarConfig,
    updateRouteConfig,
    clearCarConfig,
    refreshCarRouteConfig: fetchCarRouteConfig,
  }
}

export default useCarRouteConfig
